<template>
  <div 
    :class="[
      'test-card',
      { 'test-card-selected': isSelected },
      { 'test-card-disabled': isDisabled }
    ]"
    @click="!isDisabled && $emit('click', test)"
  >
    <div class="test-card-content">
      <div class="selected-box" :class="{ checked: isSelected }">
        <span v-if="isSelected">✓</span>
      </div>
      <!-- Test Name and Badge -->
      <div class="test-info">
        <el-tooltip
          effect="dark"
          :content="test.displayName || test.name || test.bundleName"
          placement="top"
          :disabled="!isNameOverflow"
        >
          <p class="test-name" ref="testName">{{ test.displayName || test.name || test.bundleName }}</p>
        </el-tooltip>
        <div 
          v-if="showSampleType" 
          ref="sampleTypeContainer"
          class="flex items-center sample-type-container"
        >
          <TestSampleTypeTagNew
            v-for="t in sampleTypeTagInfo.keys"
            :key="t"
            :type="getAbbreviation(t)"
          />
        </div>
      </div>
      
      <!-- Price -->
      <div class="test-price">
        <p>{{ currency.symbol }}{{ test.price }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import TestSampleTypeTagNew from "../../../components/TestSampleTypeTagNew.vue";
import { getSampleTypeSetsNew, getSampleTypeTagInfoFromSetsNew } from "@/utils/va-order-test/sampleTypes.js";
import { processNameAbbreviationNew } from "@/utils/va-order-test/index.js";
import { mapState } from "vuex";

export default {
  name: "BuildBundleTestCard",
  components: {
    TestSampleTypeTagNew
  },
  props: {
    test: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    showSampleType: {
      type: Boolean,
      default: true
    }
  },
  emits: ["click"],
  data() {
    return {
      isNameOverflow: false,
      useAbbreviations: false
    };
  },
  mounted() {
    this.checkOverflow();
    this.checkSampleTypeOverflow();
    window.addEventListener('resize', this.checkOverflow);
    window.addEventListener('resize', this.checkSampleTypeOverflow);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkOverflow);
    window.removeEventListener('resize', this.checkSampleTypeOverflow);
  },
  watch: {
    sampleTypeTagInfo: {
      handler() {
        this.$nextTick(() => {
          this.checkSampleTypeOverflow();
        });
      },
      deep: true
    }
  },
  computed: {
    ...mapState(["currency"]),
    // Unified sample type tag info (keys + single)
    sampleTypeTagInfo() {
      try {
        return getSampleTypeTagInfoFromSetsNew(this.test.sampleTypeSets);
      } catch (e) {
        return { keys: [], single: "" };
      }
    },
  },
  methods: {
    checkOverflow() {
      this.$nextTick(() => {
        const el = this.$refs.testName;
        if (el) {
          this.isNameOverflow = el.scrollWidth > el.clientWidth;
        }
      });
    },
    checkSampleTypeOverflow() {
      this.$nextTick(() => {
        const container = this.$refs.sampleTypeContainer;
        if (!container || !this.showSampleType || this.sampleTypeTagInfo.keys.length === 0) {
          this.useAbbreviations = false;
          return;
        }
        
        // Check if tags overflow the available space
        const card = container.closest('.test-card');
        if (!card) return;
        
        const cardContent = card.querySelector('.test-card-content');
        if (!cardContent) return;
        
        const testPriceEl = card.querySelector('.test-price');
        if (!testPriceEl) return;
        
        // Calculate available width for sample type tags
        const cardContentWidth = cardContent.clientWidth;
        const testNameEl = this.$refs.testName;
        const testNameWidth = testNameEl ? testNameEl.offsetWidth : 0;
        const testPriceWidth = testPriceEl.offsetWidth;
        const gap = 8; // gap between elements in test-info
        const availableWidth = cardContentWidth - testNameWidth - testPriceWidth - gap;
        
        // Create a temporary hidden element to measure full name width
        const tempContainer = document.createElement('div');
        tempContainer.className = 'flex items-center sample-type-container';
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.whiteSpace = 'nowrap';
        
        // Add temporary tags with full names
        this.sampleTypeTagInfo.keys.forEach(type => {
          const tempTag = document.createElement('span');
          tempTag.className = 'sample-type-tag';
          tempTag.style.padding = '2px 10px';
          tempTag.style.fontSize = '12px';
          tempTag.style.fontFamily = '"IBM Plex Sans Hebrew"';
          tempTag.textContent = type;
          tempContainer.appendChild(tempTag);
        });
        
        document.body.appendChild(tempContainer);
        const fullNameWidth = tempContainer.offsetWidth;
        document.body.removeChild(tempContainer);
        
        // If full names overflow, use abbreviations
        this.useAbbreviations = fullNameWidth > availableWidth;
      });
    },
    getAbbreviation(type) {
      return processNameAbbreviationNew(type);
    }
  }
};
</script>

<style scoped>
.test-card {
  background: #f9fbfd;
  border: 1px solid #e3e7ea;
  border-radius: 8px;
  box-shadow: 1px 1px 3px 0 rgba(26, 32, 44, 0.18);
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 46px;
  max-height: 46px;
}

.test-card:hover:not(.test-card-disabled) {
  box-shadow: 1px 1px 4px 0 rgba(26, 32, 44, 0.22);
}

.test-card-selected {
  border: 1px solid #004879;
  background: #edf6ff;
}

.test-card-disabled {
  background: #e6e6e6;
  cursor: not-allowed;
  opacity: 0.6;
}

.test-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.selected-box {
  width: 22px;
  height: 22px;
  border-radius: 3px;
  border: 1px solid #004879;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  background: #fff;
}

.selected-box.checked {
  background: #004879;
  border-color: #004879;
}

.test-info {
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-name {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}

.test-price {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  flex-shrink: 0;
  margin-left: 8px;
}
</style>
